/**
 * App — boot SWA auth + provide TenantContext + render router (Block 6.A
 * / ADR-024 D7).
 *
 * Phase 0 verification UI removed; replaced with the directory app.
 * Boot flow:
 *   1. Fetch /.auth/me. Before any render for the answer, tell the page
 *      restore guard whose page this is and claim the browser's storage
 *      for that person (src/lib/signedInStorage.ts); nobody signed in
 *      removes the cross-tab signal key. principal = null → render Sign-in
 *      CTA; a failed check renders it too, and leaves the key.
 *   2. principal = signed-in → fetch whoami. Set TenantContext.
 *   3. Render RouterProvider with TenantContext available to all routes.
 *
 * D7-bis: defaultTenant = 'peace' is consumed by routes.tsx for the root
 * redirect; whoami reconciliation replaces the URL if a different tenant
 * resolves (Era B; never Phase 1 single-tenant).
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { RouterProvider } from 'react-router-dom';
import {
  TenantContext,
  defaultTenant,
  fetchPrincipal,
  fetchWhoami,
  type TenantState,
} from '@/lib/auth';
import { UndoProvider } from '@/lib/undo';
import { UndoToast } from '@/components/UndoToast';
import { IdleSignOut } from '@/components/IdleSignOut';
import { BusyNotice } from '@/components/BusyNotice';
import { FloatingNotices } from '@/components/FloatingNotices';
import { SignInScreen, signedOutReasonFromSearch } from '@/components/SignInScreen';
import { router } from '@/routes';
import { setAuthenticatedUser } from '@/lib/telemetry';
import { rememberPageUser } from '@/lib/pageRestoreGuard';
import { flushKeptWorkReport } from '@/lib/keptWork';
import { claimStorageFor, forgetSignedInUser } from '@/lib/signedInStorage';
import type { ClientPrincipal, WhoamiResponse } from '@/lib/types';

export function App(): ReactNode {
  const [principal, setPrincipal] = useState<ClientPrincipal | null | undefined>(undefined);
  const [whoami, setWhoami] = useState<WhoamiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPrincipal()
      .then((p) => {
        // Before anything renders for this person: the restore guard learns
        // whose page this is, and the browser's storage is claimed for them
        // (a different person's drafts and hand-offs go). Its own try: a
        // storage that refuses never keeps a signed-in person from the app.
        try {
          rememberPageUser(p?.userId ?? null);
          if (p) claimStorageFor(p.userId);
          else forgetSignedInUser();
        } catch {
          // storage refused: nothing to claim
        }
        setPrincipal(p);
      })
      .catch(() => {
        // A failed check is not "nobody signed in": the signal key stays, so
        // the other open tabs do not reload on a platform blip.
        rememberPageUser(null);
        setPrincipal(null);
      });
  }, []);

  useEffect(() => {
    if (!principal) return;
    fetchWhoami(defaultTenant)
      .then((w) => {
        setWhoami(w);
        // Phase 1.15 v1 §6.2 — full-identifiable telemetry binding for
        // office/managerial user set (operator-locked 2026-05-19). Bind
        // user_id + tenant_id once whoami resolves. Reconfirm at CM rollout.
        if (w.outcome === 'success' && w.user_id && w.tenant_id) {
          setAuthenticatedUser(w.user_id, w.tenant_id);
          // A write that met an expired sign-in before this sign-in is
          // told to the Errors screen now that it can be.
          flushKeptWorkReport(defaultTenant, principal.userId);
        }
      })
      .catch(() => setError("Olive couldn't load your account."));
  }, [principal]);

  // ADR-0046 — minimal refresh seam: the Permissions tab calls this after a
  // toggle so the acting owner's affordances pick up the fresh effective
  // capability set without a full reload. Errors are swallowed (the current
  // whoami stays; the server enforces the change server-side regardless).
  const refreshWhoami = useCallback(async (): Promise<void> => {
    try {
      const w = await fetchWhoami(defaultTenant);
      setWhoami(w);
    } catch {
      /* keep the current whoami; server-side enforcement is unaffected */
    }
  }, []);

  const tenantState: TenantState = useMemo(
    () => ({
      principal,
      whoami,
      tenantSlug: defaultTenant,
      error,
      refreshWhoami,
    }),
    [principal, whoami, error, refreshWhoami],
  );

  if (principal === undefined) {
    return (
      <main className="min-h-screen flex items-center justify-center text-sm text-ink-soft">
        Loading…
      </main>
    );
  }

  if (principal === null) {
    return <SignInScreen reason={signedOutReasonFromSearch(window.location.search)} />;
  }

  return (
    <TenantContext.Provider value={tenantState}>
      <UndoProvider>
        {/* Route changes render outside React transitions, so the page on
            screen follows the URL as soon as it changes. Inside a transition
            the old page stays mounted until the new one commits, and a Back
            pressed in that gap returns to a page that never unmounted, with
            its state (the Clients search) intact. Because navigations are not
            transitions here, a lazy route or a suspending query added later
            shows its Suspense fallback on navigation instead of keeping the
            old page, so it needs a Suspense boundary of its own. */}
        <RouterProvider router={router} useTransitions={false} />
        <FloatingNotices>
          <BusyNotice />
          <UndoToast />
        </FloatingNotices>
        <IdleSignOut />
      </UndoProvider>
    </TenantContext.Provider>
  );
}
